import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ManageEmailSendingDatasource } from './datasource/manage-email-sending-datasource';
import { ManageEmailSendingService } from "@services/dws/manage-email-sending.service";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { tr } from '@util/tr/tr';
import { MatDialog } from "@angular/material/dialog";
import { ResendDialogComponent } from '@web/app/pages/admin/resend-email/resend-dialog/resend-dialog.component';
import { ResendEmailService } from "@services/dws/resend-email.service";
import { DWSLanguagesService } from '@services/dws/dws-languages.service';
import { toast } from '@web/util/toast';

@Component({
  selector: 'web-manage-email-sending',
  templateUrl: './manage-email-sending.component.html',
  styleUrls: ['./manage-email-sending.component.scss']
})
export class ManageEmailSendingComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChild("paginatorTop") topPaginator!: MatPaginator;
  @ViewChild("paginatorBottom") bottomPaginator!: MatPaginator;
  dataSource!: ManageEmailSendingDatasource;
  displayedColumns: string[] = [
    "typology",
    "state",
    "date",
    "actions"
  ];
  pageSize = 10;
  fallbackLanguage: string = this.dwsLanguagesService.getFallbackLanguageIso();
  typeLangRegex = /^(.*?-to-[^-]+)-([a-z]{3})(?=-|$)/i;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dlg: MatDialogRef<ManageEmailSendingComponent>,
    private manageEmailSendingService: ManageEmailSendingService,
    private reSendEmaildialog: MatDialog,
    private resendEmailService: ResendEmailService,
    private dwsLanguagesService: DWSLanguagesService
  ) { 
    dlg.disableClose = true;
  }

  ngOnInit(): void {
    this.setupDataSource();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => {
      this.bottomPaginator.pageIndex = 0;
      this.loadData();
    });

    this.bottomPaginator.page.subscribe(() => {
      this.loadData();
    });

    this.loadData();
  }

  private loadData(): void {
    const sortBy = this.sort.active && this.sort.direction
      ? `${this.sort.active},${this.sort.direction.toUpperCase()}`
      : "typology,DESC";

    this.dataSource.loadManageEmailSending(
      this.data.externalEntityId,
      this.bottomPaginator.pageIndex,
      this.pageSize,
      this.data.isReservationCompleted,
      sortBy
    );
  }

  listManageEmailSending(
      page: number = 0,
      count: number = 50,
      sortBy: string = "typology,DESC"
    ) {
      this.dataSource.loadManageEmailSending(
        this.data.externalEntityId,
        page,
        count,
        this.data.isReservationCompleted,
        sortBy,
      );
    }

  setupDataSource() {
    this.dataSource = new ManageEmailSendingDatasource(this.manageEmailSendingService);
  }

  closeClicked(event?: any) {
    this.dlg.close();
  }

  cancelClicked() {
    this.dlg.close();
  }

  public handlePageBottom(e: any) {
    let { pageSize } = e;
    this.pageSize = pageSize;

    if (!this.bottomPaginator.hasNextPage()) {
      this.topPaginator.lastPage();
    } else if (!this.bottomPaginator.hasPreviousPage()) {
      this.topPaginator.firstPage();
    } else {
      if (this.bottomPaginator.pageIndex < this.topPaginator.pageIndex) {
        this.topPaginator.previousPage();
      } else if (this.bottomPaginator.pageIndex > this.topPaginator.pageIndex) {
        this.topPaginator.nextPage();
      }
    }
  }

  getState(sendAt: string, processedAt: string) {
    if (processedAt && processedAt === "1970-01-01T00:00:00") {
      return "Inactive";
    } else if (new Date(sendAt) > new Date()) {
      return "Generated feminine";
    } else {
      return "send";
    }
  }

  getDate(sendAt: string, processedAt: string, updatedAt: string) {
    if (processedAt && processedAt === "1970-01-01T00:00:00") {
      if (new Date(sendAt) > new Date()) {
          return tr("Deactivated on: ") + new Date(updatedAt).toLocaleString();
      } else {
          return tr("Deactivated on: ") + new Date(updatedAt).toLocaleString() + " " + tr("Scheduled time expired");
      }
    } else if (new Date(sendAt) > new Date()) {
      return tr("Expected dispatch: ") + new Date(sendAt).toLocaleString();
    } else {
      return tr("Sent on") + ": " + new Date(sendAt).toLocaleString();
    }
  }

  actionSelected(action: any) {
    switch (action.id) {
      case "re-send":
        this.openResendDialog(action.type);
        break;
      case "deactivate":
        this.deactivateEmailMessage(action.emailMessageId);
        break;
      case "reactivate":
        this.reactivateEmailMessage(action.emailMessageId);
        break;
      case "send-manually":
        this.openResendDialog(action.type);
        break;
    }
  }

  deactivateEmailMessage(emailMessageId: number) {
    this.dataSource.deactivateEmailMessage(emailMessageId).subscribe({
      next: () => {
        toast(tr("Email deactivated"));
        this.loadData();
      },
      error: (err) => {
        console.error(err);
        toast(tr("Failed to deactivate email"));
      }
    });
  }

  reactivateEmailMessage(emailMessageId: number) {
    this.dataSource.reactivateEmailMessage(emailMessageId).subscribe({
      next: () => {
        toast(tr("Email reactivated"));
        this.listManageEmailSending(
          this.bottomPaginator.pageIndex,
          this.pageSize,
          "typology,DESC"
        );
      },
      error: (err) => {
        console.error(err);
        toast(tr("Failed to reactivate email"));
      }
    });
  }

  openResendDialog(type: string) {
    let reservationType = null;
    let language = null;
    const parts = type.replace("reservation-", "").split("-");
    const langIndex = parts.findIndex(p => /^[a-z]{3}$/i.test(p));
    if (langIndex !== -1) {
      language = parts[langIndex];
      parts.splice(langIndex, 1);
    }
    reservationType = parts.slice(0, 3).join("-");
    const dialogRef = this.reSendEmaildialog.open(ResendDialogComponent, {
      width: "600px",
      height: "auto",
      data: {
        wineryId: this.data.wineryId,
        experienceId: this.data.experienceId,
        reservationId: this.data.externalEntityId,
        forcedLanguage: language,
        emailAddress: this.data.emailAddress
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !result.endpoint) return;
      this.resendEmailService.resendEmail(result.endpoint, result).subscribe({
        next: (res) => {
          toast(tr("Email resent successfully!"));
        },
        error: (err) => {
          console.error("Error", err);
          toast(tr("Failed to resend email."));
        },
      });
    });
  }

  getDateHeader() {
    return tr("Date") + "/" + tr("Hour");
  }

  /**
   * Extracts language code from type (e.g., "reservation-create-to-guest-ita" -> "it")
   * @param type type
   * @returns language iso code
   */
  extractLanguageFromType(type: string): string {
    const match = type.match(this.typeLangRegex);
    if (match) {
      const lang3 = match[2];
      return this.dwsLanguagesService.mapFromLangSuffix(lang3);
    }
    return this.fallbackLanguage;
  }
}
